/**
 * sqlite3mt durable IndexedDB record codecs and store
 *
 * This module fixes the persisted record shapes for the §6 durable store and
 * implements the concrete IndexedDB-backed DurableLocalStore on top of them.
 * The codec layer remains the canonical on-disk shape so bootstrap, commit
 * registration, and reconcile code all share the same serialization rules.
 */

import { openDB, type DBSchema, type IDBPDatabase, type IDBPObjectStore } from 'idb'

import type { DurableReconcileIntent } from './bootstrap'
import type {
    DurableLocalCommitJournalEntry,
    DurableLocalSnapshot,
    DurableLocalStore,
    DurableStoreTransaction,
    ReconcileAppendAttempt,
    ReconcileCommitMeta,
} from './durable'
import type {
    Delta,
    FrameMax,
    PageHash,
    Pgno,
    PublicHead,
    ReconcileIntentPhase,
    Root,
    RootId,
    WalSnapshot,
} from './types'

const BIGINT_KEY_PREFIX_WIDTH = 6

/**
 * Durable-store namespace version.
 *
 * v2 intentionally invalidates older browser-local sqlite3mt durable stores.
 * In practice we observed bootstrap/open hangs on some long-lived browsers
 * after substantial sqlite3mt multithread/runtime changes while the on-disk
 * durable state still came from older builds. A namespace bump is the safest
 * way to force a clean bootstrap without mixing old snapshot/journal state
 * into the current runtime.
 */
export const SQLITE3MT_DURABLE_IDB_NAME = 'secureblob-sqlite3mt-durable-v2'
export const SQLITE3MT_DURABLE_IDB_VERSION = 2
export const SQLITE3MT_SNAPSHOT_SINGLETON_KEY = 'snapshot'
export const SQLITE3MT_RECONCILE_INTENT_SINGLETON_KEY = 'reconcile-intent'
const DEFAULT_DURABLE_IDB_OPEN_TIMEOUT_MS = 15_000

/**
 * Build a stable durable-store name for one sqlite3mt local runtime identity.
 *
 * sqlite3mt snapshots/journal/intents are WAL-local state. Reusing the same
 * IndexedDB name across multiple `.db` files or multiple live runtimes lets one
 * runtime replay another runtime's local journal/snapshot during bootstrap.
 */
export function buildSqlite3MtDurableIdbName(scope: string): string {
    return `${SQLITE3MT_DURABLE_IDB_NAME}:${encodeURIComponent(scope)}`
}

export interface Sqlite3MtBlobRecord {
    hash: string
    bytes: Uint8Array
}

export interface Sqlite3MtManifestNodeRecord {
    hash: string
    bytes: Uint8Array
}

export interface EncodedRoot {
    rootId: string
    meta: {
        manifestRootId: string
        pageCount: number
        parentPublicRootId: string | null
    }
}

export interface EncodedPublicHead {
    root: EncodedRoot
    version: string
}

export interface EncodedDelta {
    pageHashes: Array<readonly [number, string]>
    postPageCount: number | null
}

export interface Sqlite3MtJournalEntryRecord {
    commitSeqKey: string
    commitSeq: string
    workspaceBaseRoot: EncodedRoot
    workspaceBaseVersion: string
    workspaceBasePageCount: number
    walSalt0: number
    walSalt1: number
    frameMax: number
    delta: EncodedDelta
}

export interface Sqlite3MtSnapshotRecord {
    singletonKey: typeof SQLITE3MT_SNAPSHOT_SINGLETON_KEY
    schemaVersion: 3
    dbBaseRoot: EncodedRoot
    dbOverlay: Array<readonly [number, string]>
    dbPageCount: number
    workspaceBaseHead: EncodedPublicHead
    workspaceOverlay: Array<readonly [number, string]>
    workspacePageCount: number
    pendingDbDeltas: Array<readonly [number, EncodedDelta]>
    currentWalSalt0: number | null
    currentWalSalt1: number | null
    integratedFrameMax: number
    lastWalSnapshot: WalSnapshot | null
    localCommitSeq: string
    requiredBlobHashes: string[]
    requiredManifestNodeHashes: string[]
}

export interface Sqlite3MtReconcileIntentRecord {
    singletonKey: typeof SQLITE3MT_RECONCILE_INTENT_SINGLETON_KEY
    intentId: string
    phase: ReconcileIntentPhase
    baseRoot: EncodedRoot
    baseVersion: string
    basePageCount: number
    remoteRoot: EncodedRoot
    remoteVersion: string
    walSalt0: number
    walSalt1: number
    caughtUpFrameMax: number
    importDelta: EncodedDelta
    residualOverlay: Array<readonly [number, string]>
    residualPageCount: number
    attemptStartFrame: number | null
    attemptEndFrame: number | null
    attemptWalSalt0: number | null
    attemptWalSalt1: number | null
    commitFrameMax: number | null
    commitPostWalSalt0: number | null
    commitPostWalSalt1: number | null
}

export interface Sqlite3MtDurableIdbSchema extends DBSchema {
    blobs: {
        key: string
        value: Sqlite3MtBlobRecord
    }
    manifestNodes: {
        key: string
        value: Sqlite3MtManifestNodeRecord
    }
    journal: {
        key: string
        value: Sqlite3MtJournalEntryRecord
    }
    snapshot: {
        key: typeof SQLITE3MT_SNAPSHOT_SINGLETON_KEY
        value: Sqlite3MtSnapshotRecord
    }
    reconcileIntent: {
        key: typeof SQLITE3MT_RECONCILE_INTENT_SINGLETON_KEY
        value: Sqlite3MtReconcileIntentRecord
    }
}

export class IdbDurableLocalStore implements DurableLocalStore {
    private readonly dbPromise: Promise<IDBPDatabase<Sqlite3MtDurableIdbSchema>>

    constructor(
        dbName: string = SQLITE3MT_DURABLE_IDB_NAME,
        options?: { openTimeoutMs?: number },
    ) {
        this.dbPromise = openDurableIdbWithTimeout(
            dbName,
            options?.openTimeoutMs ?? DEFAULT_DURABLE_IDB_OPEN_TIMEOUT_MS,
        )
    }

    async close(): Promise<void> {
        try {
            const db = await this.db()
            db.close()
        } catch {
            // Closing an unopened or failed durable store is cleanup, not a
            // second bootstrap failure surface.
        }
    }

    async putBlob(hash: PageHash, bytes: Uint8Array): Promise<void> {
        const db = await this.db()
        try {
            await db.add('blobs', {
                hash: hash as string,
                bytes: cloneBytes(bytes),
            })
        } catch (error) {
            if (!isConstraintError(error)) {
                throw error
            }
        }
    }

    async getBlob(hash: PageHash): Promise<Uint8Array | null> {
        const record = await (await this.db()).get('blobs', hash as string)
        return record == null ? null : cloneBytes(record.bytes)
    }

    async putManifestNode(hash: string, bytes: Uint8Array): Promise<void> {
        const db = await this.db()
        try {
            await db.add('manifestNodes', {
                hash,
                bytes: cloneBytes(bytes),
            })
        } catch (error) {
            if (!isConstraintError(error)) {
                throw error
            }
        }
    }

    async getManifestNode(hash: string): Promise<Uint8Array | null> {
        const record = await (await this.db()).get('manifestNodes', hash)
        return record == null ? null : cloneBytes(record.bytes)
    }

    async readJournalAfter(afterSeq: bigint): Promise<DurableLocalCommitJournalEntry[]> {
        const query =
            afterSeq < 0n
                ? undefined
                : IDBKeyRange.lowerBound(encodeLexicographicBigIntKey(afterSeq), true)
        const records = await (await this.db()).getAll('journal', query)
        return records.map(deserializeDurableJournalEntry)
    }

    async truncateJournalThrough(throughSeq: bigint): Promise<void> {
        if (throughSeq < 0n) {
            return
        }

        await (await this.db()).delete(
            'journal',
            IDBKeyRange.upperBound(encodeLexicographicBigIntKey(throughSeq))
        )
    }

    async transaction<T>(
        callback: (tx: DurableStoreTransaction) => Promise<T> | T
    ): Promise<T> {
        const pendingBlobs = new Map<PageHash, Uint8Array>()
        const pendingCommits: DurableLocalCommitJournalEntry[] = []

        const tx: DurableStoreTransaction = {
            putBlob(hash: PageHash, bytes: Uint8Array): void | Promise<void> {
                if (!pendingBlobs.has(hash)) {
                    pendingBlobs.set(hash, cloneBytes(bytes))
                }
            },
            appendLocalCommit(entry: DurableLocalCommitJournalEntry): void | Promise<void> {
                pendingCommits.push(deserializeDurableJournalEntry(serializeDurableJournalEntry(entry)))
            },
        }

        const result = await callback(tx)

        const db = await this.db()
        const idbTx = db.transaction(['blobs', 'journal'], 'readwrite')
        try {
            const lastCommittedSeq = await readLastCommittedSeq(
                idbTx.objectStore('journal')
            )
            assertJournalAppendOrder(lastCommittedSeq, pendingCommits)

            const blobsStore = idbTx.objectStore('blobs')
            const journalStore = idbTx.objectStore('journal')

            for (const [hash, bytes] of pendingBlobs) {
                const existing = await blobsStore.get(hash as string)
                if (existing != null) {
                    if (!bytesEqual(existing.bytes, bytes)) {
                        throw new Error(`durable blob hash collision for ${hash as string}`)
                    }
                    continue
                }

                await blobsStore.put({
                    hash: hash as string,
                    bytes,
                })
            }

            for (const entry of pendingCommits) {
                await journalStore.add(serializeDurableJournalEntry(entry))
            }

            await idbTx.done
            return result
        } catch (error) {
            await abortTransaction(idbTx)
            throw error
        }
    }

    async loadSnapshot(): Promise<DurableLocalSnapshot | null> {
        const record = await (await this.db()).get(
            'snapshot',
            SQLITE3MT_SNAPSHOT_SINGLETON_KEY
        )
        return record == null ? null : deserializeDurableSnapshot(record)
    }

    async writeSnapshot(snapshot: DurableLocalSnapshot): Promise<void> {
        const db = await this.db()
        const tx = db.transaction(['snapshot', 'blobs', 'manifestNodes'], 'readwrite')
        try {
            await tx.objectStore('snapshot').put(serializeDurableSnapshot(snapshot))
            await deleteStoreRecordsNotInSet(tx.objectStore('blobs'), stringSet(snapshot.requiredBlobHashes))
            await deleteStoreRecordsNotInSet(tx.objectStore('manifestNodes'), stringSet(snapshot.requiredManifestNodeHashes))
            await tx.done
        } catch (error) {
            await abortTransaction(tx)
            throw error
        }
    }

    async loadReconcileIntentOrNull(): Promise<DurableReconcileIntent | null> {
        const record = await (await this.db()).get(
            'reconcileIntent',
            SQLITE3MT_RECONCILE_INTENT_SINGLETON_KEY
        )
        return record == null ? null : deserializeDurableReconcileIntent(record)
    }

    async writeReconcileIntent(intent: DurableReconcileIntent): Promise<void> {
        await (await this.db()).put(
            'reconcileIntent',
            serializeDurableReconcileIntent(intent)
        )
    }

    async markReconcileIntentAppending(
        intentId: bigint,
        attempt: ReconcileAppendAttempt
    ): Promise<void> {
        const db = await this.db()
        const tx = db.transaction('reconcileIntent', 'readwrite')
        try {
            const store = tx.objectStore('reconcileIntent')
            const record = await store.get(SQLITE3MT_RECONCILE_INTENT_SINGLETON_KEY)
            if (record == null) {
                throw new Error(`no reconcile intent to mark APPENDING (intentId=${intentId})`)
            }

            const intent = deserializeDurableReconcileIntent(record)
            if (intent.intentId !== intentId) {
                throw new Error(
                    `intentId mismatch: expected ${intent.intentId}, got ${intentId}`
                )
            }
            if (intent.phase !== 'PREPARED') {
                throw new Error(
                    `can only mark APPENDING from PREPARED, got ${intent.phase}`
                )
            }

            await store.put(
                serializeDurableReconcileIntent({
                    ...intent,
                    phase: 'APPENDING',
                    attemptStartFrame: attempt.attemptStartFrame,
                    attemptEndFrame: attempt.attemptEndFrame,
                    attemptWalSalt0: attempt.attemptWalSalt0,
                    attemptWalSalt1: attempt.attemptWalSalt1,
                })
            )
            await tx.done
        } catch (error) {
            await abortTransaction(tx)
            throw error
        }
    }

    async markReconcileIntentCommitted(
        intentId: bigint,
        committed: ReconcileCommitMeta
    ): Promise<void> {
        const db = await this.db()
        const tx = db.transaction('reconcileIntent', 'readwrite')
        try {
            const store = tx.objectStore('reconcileIntent')
            const record = await store.get(SQLITE3MT_RECONCILE_INTENT_SINGLETON_KEY)
            if (record == null) {
                throw new Error(`no reconcile intent to mark COMMITTED (intentId=${intentId})`)
            }

            const intent = deserializeDurableReconcileIntent(record)
            if (intent.intentId !== intentId) {
                throw new Error(
                    `intentId mismatch: expected ${intent.intentId}, got ${intentId}`
                )
            }
            if (intent.phase !== 'APPENDING') {
                throw new Error(
                    `can only mark COMMITTED from APPENDING, got ${intent.phase}`
                )
            }

            await store.put(
                serializeDurableReconcileIntent({
                    ...intent,
                    phase: 'COMMITTED',
                    commitFrameMax: committed.commitFrameMax,
                    commitPostWalSalt0: committed.commitPostWalSalt0,
                    commitPostWalSalt1: committed.commitPostWalSalt1,
                })
            )
            await tx.done
        } catch (error) {
            await abortTransaction(tx)
            throw error
        }
    }

    async clearReconcileIntent(intentId: bigint): Promise<void> {
        const db = await this.db()
        const tx = db.transaction('reconcileIntent', 'readwrite')
        try {
            const store = tx.objectStore('reconcileIntent')
            const record = await store.get(SQLITE3MT_RECONCILE_INTENT_SINGLETON_KEY)
            if (record == null) {
                await tx.done
                return
            }

            const intent = deserializeDurableReconcileIntent(record)
            if (intent.intentId !== intentId) {
                throw new Error(
                    `intentId mismatch: expected ${intent.intentId}, got ${intentId}`
                )
            }

            await store.delete(SQLITE3MT_RECONCILE_INTENT_SINGLETON_KEY)
            await tx.done
        } catch (error) {
            await abortTransaction(tx)
            throw error
        }
    }

    async clearAllLocalState(): Promise<void> {
        const db = await this.db()
        const tx = db.transaction([
            'blobs',
            'manifestNodes',
            'journal',
            'snapshot',
            'reconcileIntent',
        ], 'readwrite')
        try {
            await tx.objectStore('blobs').clear()
            await tx.objectStore('manifestNodes').clear()
            await tx.objectStore('journal').clear()
            await tx.objectStore('snapshot').clear()
            await tx.objectStore('reconcileIntent').clear()
            await tx.done
        } catch (error) {
            await abortTransaction(tx)
            throw error
        }
    }

    private async db(): Promise<IDBPDatabase<Sqlite3MtDurableIdbSchema>> {
        return await this.dbPromise
    }
}

async function deleteStoreRecordsNotInSet<StoreName extends 'blobs' | 'manifestNodes'>(
    store: IDBPObjectStore<
        Sqlite3MtDurableIdbSchema,
        Array<'snapshot' | 'blobs' | 'manifestNodes'>,
        StoreName,
        'readwrite'
    >,
    keep: ReadonlySet<string>,
): Promise<void> {
    let cursor = await store.openCursor()
    while (cursor) {
        const key = typeof cursor.key === 'string' ? cursor.key : String(cursor.key)
        if (!keep.has(key)) {
            await cursor.delete()
        }
        cursor = await cursor.continue()
    }
}

function stringSet(values: Iterable<unknown>): Set<string> {
    return new Set(Array.from(values, value => String(value)))
}

function openDurableIdbWithTimeout(
    dbName: string,
    timeoutMs: number,
): Promise<IDBPDatabase<Sqlite3MtDurableIdbSchema>> {
    const boundedTimeoutMs = Number.isFinite(timeoutMs)
        ? Math.max(1, Math.trunc(timeoutMs))
        : DEFAULT_DURABLE_IDB_OPEN_TIMEOUT_MS
    let blocked = false
    let settled = false
    let timeout: ReturnType<typeof setTimeout> | null = null

    const openPromise = openDB<Sqlite3MtDurableIdbSchema>(
        dbName,
        SQLITE3MT_DURABLE_IDB_VERSION,
        {
            upgrade(db) {
                if (!db.objectStoreNames.contains('blobs')) {
                    db.createObjectStore('blobs', { keyPath: 'hash' })
                }
                if (!db.objectStoreNames.contains('manifestNodes')) {
                    db.createObjectStore('manifestNodes', { keyPath: 'hash' })
                }
                if (!db.objectStoreNames.contains('journal')) {
                    db.createObjectStore('journal', { keyPath: 'commitSeqKey' })
                }
                if (!db.objectStoreNames.contains('snapshot')) {
                    db.createObjectStore('snapshot', { keyPath: 'singletonKey' })
                }
                if (!db.objectStoreNames.contains('reconcileIntent')) {
                    db.createObjectStore('reconcileIntent', { keyPath: 'singletonKey' })
                }
            },
            blocked() {
                blocked = true
            },
            blocking(_currentVersion, _blockedVersion, event) {
                const db = (event.target as IDBOpenDBRequest | null)?.result
                db?.close()
            },
        }
    )

    const timed = new Promise<IDBPDatabase<Sqlite3MtDurableIdbSchema>>((resolve, reject) => {
        timeout = setTimeout(() => {
            if (settled) return
            settled = true
            reject(new Error(
                `sqlite3mt durable IndexedDB open timed out after ${boundedTimeoutMs}ms for ${dbName}` +
                (blocked ? '; open is blocked by another tab or stale browser connection' : '')
            ))
        }, boundedTimeoutMs)

        openPromise.then(
            db => {
                if (settled) {
                    db.close()
                    return
                }
                settled = true
                if (timeout != null) clearTimeout(timeout)
                resolve(db)
            },
            error => {
                if (settled) return
                settled = true
                if (timeout != null) clearTimeout(timeout)
                reject(error)
            },
        )
    })

    return timed
}

export function encodeLexicographicBigIntKey(value: bigint): string {
    const digits = encodeBigInt(value, 'value')
    if (digits.length > (10 ** BIGINT_KEY_PREFIX_WIDTH) - 1) {
        throw new RangeError(
            `bigint value is too wide for a ${BIGINT_KEY_PREFIX_WIDTH}-digit key prefix`
        )
    }

    return `${digits.length.toString(10).padStart(BIGINT_KEY_PREFIX_WIDTH, '0')}:${digits}`
}

export function decodeLexicographicBigIntKey(key: string): bigint {
    if (!/^\d{6}:\d+$/.test(key)) {
        throw new TypeError(`invalid lexicographic bigint key: ${key}`)
    }

    const declaredLength = Number(key.slice(0, BIGINT_KEY_PREFIX_WIDTH))
    const digits = key.slice(BIGINT_KEY_PREFIX_WIDTH + 1)
    if (digits.length !== declaredLength) {
        throw new TypeError(
            `lexicographic bigint key length mismatch: expected ${declaredLength}, got ${digits.length}`
        )
    }

    if (digits !== '0' && digits.startsWith('0')) {
        throw new TypeError(`lexicographic bigint key is not canonical: ${key}`)
    }

    return decodeBigInt(digits, 'key')
}

export function serializeDurableJournalEntry(
    entry: DurableLocalCommitJournalEntry
): Sqlite3MtJournalEntryRecord {
    return {
        commitSeqKey: encodeLexicographicBigIntKey(entry.commitSeq),
        commitSeq: encodeBigInt(entry.commitSeq, 'commitSeq'),
        workspaceBaseRoot: encodeRoot(entry.workspaceBaseRoot),
        workspaceBaseVersion: encodeBigInt(entry.workspaceBaseVersion, 'workspaceBaseVersion'),
        workspaceBasePageCount: entry.workspaceBasePageCount,
        walSalt0: entry.walSalt0,
        walSalt1: entry.walSalt1,
        frameMax: entry.frameMax as number,
        delta: encodeDelta(entry.delta),
    }
}

export function deserializeDurableJournalEntry(
    record: Sqlite3MtJournalEntryRecord
): DurableLocalCommitJournalEntry {
    const commitSeq = decodeBigInt(record.commitSeq, 'commitSeq')
    const commitSeqFromKey = decodeLexicographicBigIntKey(record.commitSeqKey)
    if (commitSeq !== commitSeqFromKey) {
        throw new TypeError(
            `commitSeq key/value mismatch: ${record.commitSeqKey} != ${record.commitSeq}`
        )
    }

    return {
        commitSeq,
        workspaceBaseRoot: decodeRoot(record.workspaceBaseRoot),
        workspaceBaseVersion: decodeBigInt(
            record.workspaceBaseVersion,
            'workspaceBaseVersion'
        ),
        workspaceBasePageCount: record.workspaceBasePageCount,
        walSalt0: record.walSalt0,
        walSalt1: record.walSalt1,
        frameMax: record.frameMax as FrameMax,
        delta: decodeDelta(record.delta),
    }
}

export function serializeDurableSnapshot(
    snapshot: DurableLocalSnapshot
): Sqlite3MtSnapshotRecord {
    return {
        singletonKey: SQLITE3MT_SNAPSHOT_SINGLETON_KEY,
        schemaVersion: snapshot.schemaVersion,
        dbBaseRoot: encodeRoot(snapshot.dbBaseRoot),
        dbOverlay: encodePageHashEntries(snapshot.dbOverlay),
        dbPageCount: snapshot.dbPageCount,
        workspaceBaseHead: encodePublicHead(snapshot.workspaceBaseHead),
        workspaceOverlay: encodePageHashEntries(snapshot.workspaceOverlay),
        workspacePageCount: snapshot.workspacePageCount,
        pendingDbDeltas: encodeFrameDeltaEntries(snapshot.pendingDbDeltas),
        currentWalSalt0: snapshot.currentWalSalt0,
        currentWalSalt1: snapshot.currentWalSalt1,
        integratedFrameMax: snapshot.integratedFrameMax,
        lastWalSnapshot: encodeWalSnapshot(snapshot.lastWalSnapshot),
        localCommitSeq: encodeBigInt(snapshot.localCommitSeq, 'localCommitSeq'),
        requiredBlobHashes: sortStrings(snapshot.requiredBlobHashes),
        requiredManifestNodeHashes: sortStrings(snapshot.requiredManifestNodeHashes),
    }
}

export function deserializeDurableSnapshot(
    record: Sqlite3MtSnapshotRecord
): DurableLocalSnapshot {
    if (record.singletonKey !== SQLITE3MT_SNAPSHOT_SINGLETON_KEY) {
        throw new TypeError(
            `invalid durable snapshot singleton key: ${record.singletonKey}`
        )
    }
    if (record.schemaVersion !== 3) {
        throw new TypeError(
            `unsupported durable snapshot schemaVersion: ${record.schemaVersion}`
        )
    }

    return {
        schemaVersion: record.schemaVersion,
        dbBaseRoot: decodeRoot(record.dbBaseRoot),
        dbOverlay: decodePageHashEntries(record.dbOverlay, 'dbOverlay'),
        dbPageCount: record.dbPageCount,
        workspaceBaseHead: decodePublicHead(record.workspaceBaseHead),
        workspaceOverlay: decodePageHashEntries(
            record.workspaceOverlay,
            'workspaceOverlay'
        ),
        workspacePageCount: record.workspacePageCount,
        pendingDbDeltas: decodeFrameDeltaEntries(record.pendingDbDeltas),
        currentWalSalt0: record.currentWalSalt0,
        currentWalSalt1: record.currentWalSalt1,
        integratedFrameMax: record.integratedFrameMax,
        lastWalSnapshot: decodeWalSnapshot(record.lastWalSnapshot),
        localCommitSeq: decodeBigInt(record.localCommitSeq, 'localCommitSeq'),
        requiredBlobHashes: new Set(
            record.requiredBlobHashes.map(hash => hash as PageHash)
        ),
        requiredManifestNodeHashes: new Set(record.requiredManifestNodeHashes),
    }
}

export function serializeDurableReconcileIntent(
    intent: DurableReconcileIntent
): Sqlite3MtReconcileIntentRecord {
    return {
        singletonKey: SQLITE3MT_RECONCILE_INTENT_SINGLETON_KEY,
        intentId: encodeBigInt(intent.intentId, 'intentId'),
        phase: intent.phase,
        baseRoot: encodeRoot(intent.baseRoot),
        baseVersion: encodeBigInt(intent.baseVersion, 'baseVersion'),
        basePageCount: intent.basePageCount,
        remoteRoot: encodeRoot(intent.remoteRoot),
        remoteVersion: encodeBigInt(intent.remoteVersion, 'remoteVersion'),
        walSalt0: intent.walSalt0,
        walSalt1: intent.walSalt1,
        caughtUpFrameMax: intent.caughtUpFrameMax,
        importDelta: encodeDelta(intent.importDelta),
        residualOverlay: encodePageHashEntries(intent.residualOverlay),
        residualPageCount: intent.residualPageCount,
        attemptStartFrame: intent.attemptStartFrame,
        attemptEndFrame: intent.attemptEndFrame,
        attemptWalSalt0: intent.attemptWalSalt0,
        attemptWalSalt1: intent.attemptWalSalt1,
        commitFrameMax: intent.commitFrameMax,
        commitPostWalSalt0: intent.commitPostWalSalt0,
        commitPostWalSalt1: intent.commitPostWalSalt1,
    }
}

export function deserializeDurableReconcileIntent(
    record: Sqlite3MtReconcileIntentRecord
): DurableReconcileIntent {
    if (record.singletonKey !== SQLITE3MT_RECONCILE_INTENT_SINGLETON_KEY) {
        throw new TypeError(
            `invalid reconcile intent singleton key: ${record.singletonKey}`
        )
    }

    return {
        intentId: decodeBigInt(record.intentId, 'intentId'),
        phase: record.phase,
        baseRoot: decodeRoot(record.baseRoot),
        baseVersion: decodeBigInt(record.baseVersion, 'baseVersion'),
        basePageCount: record.basePageCount,
        remoteRoot: decodeRoot(record.remoteRoot),
        remoteVersion: decodeBigInt(record.remoteVersion, 'remoteVersion'),
        walSalt0: record.walSalt0,
        walSalt1: record.walSalt1,
        caughtUpFrameMax: record.caughtUpFrameMax,
        importDelta: decodeDelta(record.importDelta),
        residualOverlay: decodePageHashEntries(
            record.residualOverlay,
            'residualOverlay'
        ),
        residualPageCount: record.residualPageCount,
        attemptStartFrame: record.attemptStartFrame,
        attemptEndFrame: record.attemptEndFrame,
        attemptWalSalt0: record.attemptWalSalt0,
        attemptWalSalt1: record.attemptWalSalt1,
        commitFrameMax: record.commitFrameMax,
        commitPostWalSalt0: record.commitPostWalSalt0,
        commitPostWalSalt1: record.commitPostWalSalt1,
    }
}

function sortStrings(values: Iterable<string>): string[] {
    return [...values].sort((a, b) => a.localeCompare(b))
}

async function readLastCommittedSeq(
    journalStore: {
        openCursor(
            query?: IDBValidKey | IDBKeyRange | null,
            direction?: IDBCursorDirection
        ): Promise<{ value: Sqlite3MtJournalEntryRecord } | null>
    }
): Promise<bigint | null> {
    const cursor = await journalStore.openCursor(null, 'prev')
    if (cursor == null) {
        return null
    }

    return deserializeDurableJournalEntry(cursor.value).commitSeq
}

async function abortTransaction(
    tx: {
        abort(): void
        done: Promise<unknown>
    }
): Promise<void> {
    try {
        tx.abort()
    } catch {
        // Transaction may already be complete or aborted.
    }

    try {
        await tx.done
    } catch {
        // The original failure is more relevant than abort noise.
    }
}

function cloneBytes(bytes: Uint8Array): Uint8Array {
    return new Uint8Array(bytes)
}

function bytesEqual(a: Uint8Array, b: Uint8Array): boolean {
    if (a.byteLength !== b.byteLength) {
        return false
    }

    for (let i = 0; i < a.byteLength; i++) {
        if (a[i] !== b[i]) {
            return false
        }
    }

    return true
}

function isConstraintError(error: unknown): boolean {
    return error instanceof DOMException && error.name === 'ConstraintError'
}

function assertJournalAppendOrder(
    previousCommittedSeq: bigint | null,
    pending: DurableLocalCommitJournalEntry[]
): void {
    let previousSeq = previousCommittedSeq
    for (const entry of pending) {
        if (previousSeq != null && entry.commitSeq <= previousSeq) {
            throw new Error(
                'local commit journal must append in strictly increasing commitSeq order'
            )
        }
        previousSeq = entry.commitSeq
    }
}

function encodeRoot(root: Root): EncodedRoot {
    return {
        rootId: root.rootId as string,
        meta: {
            manifestRootId: root.meta.manifestRootId,
            pageCount: root.meta.pageCount,
            parentPublicRootId: root.meta.parentPublicRootId as string | null,
        },
    }
}

function decodeRoot(encoded: EncodedRoot): Root {
    return {
        rootId: encoded.rootId as RootId,
        meta: {
            manifestRootId: encoded.meta.manifestRootId,
            pageCount: encoded.meta.pageCount,
            parentPublicRootId: encoded.meta.parentPublicRootId as RootId | null,
        },
    }
}

function encodePublicHead(head: PublicHead): EncodedPublicHead {
    return {
        root: encodeRoot(head.root),
        version: encodeBigInt(head.version, 'version'),
    }
}

function decodePublicHead(encoded: EncodedPublicHead): PublicHead {
    return {
        root: decodeRoot(encoded.root),
        version: decodeBigInt(encoded.version, 'version'),
    }
}

function encodeDelta(delta: Delta): EncodedDelta {
    return {
        pageHashes: encodePageHashEntries(delta.pageHashes),
        postPageCount: delta.postPageCount,
    }
}

function decodeDelta(encoded: EncodedDelta): Delta {
    return {
        pageHashes: decodePageHashEntries(encoded.pageHashes, 'delta.pageHashes'),
        postPageCount: encoded.postPageCount,
    }
}

function encodePageHashEntries(
    hashes: ReadonlyMap<Pgno, PageHash>
): Array<readonly [number, string]> {
    return [...hashes]
        .map(([pgno, hash]) => [pgno as number, hash as string] as const)
        .sort((a, b) => a[0] - b[0])
}

function decodePageHashEntries(
    entries: ReadonlyArray<readonly [number, string]>,
    fieldName: string
): Map<Pgno, PageHash> {
    const result = new Map<Pgno, PageHash>()
    for (const [rawPgno, rawHash] of entries) {
        if (result.has(rawPgno as Pgno)) {
            throw new TypeError(`${fieldName} contains duplicate pgno ${rawPgno}`)
        }
        result.set(rawPgno as Pgno, rawHash as PageHash)
    }
    return result
}

function encodeFrameDeltaEntries(
    deltas: ReadonlyMap<FrameMax, Delta>
): Array<readonly [number, EncodedDelta]> {
    return [...deltas]
        .map(([frameMax, delta]) => [frameMax as number, encodeDelta(delta)] as const)
        .sort((a, b) => a[0] - b[0])
}

function decodeFrameDeltaEntries(
    entries: ReadonlyArray<readonly [number, EncodedDelta]>
): Map<FrameMax, Delta> {
    const result = new Map<FrameMax, Delta>()
    for (const [rawFrameMax, delta] of entries) {
        const frameMax = rawFrameMax as FrameMax
        if (result.has(frameMax)) {
            throw new TypeError(`pendingDbDeltas contains duplicate frameMax ${rawFrameMax}`)
        }
        result.set(frameMax, decodeDelta(delta))
    }
    return result
}

function encodeWalSnapshot(snapshot: WalSnapshot | null): WalSnapshot | null {
    return snapshot == null ? null : { ...snapshot }
}

function decodeWalSnapshot(snapshot: WalSnapshot | null): WalSnapshot | null {
    return snapshot == null ? null : { ...snapshot }
}

function encodeBigInt(value: bigint, fieldName: string): string {
    if (value < 0n) {
        throw new RangeError(`${fieldName} must be non-negative`)
    }

    return value.toString(10)
}

function decodeBigInt(value: string, fieldName: string): bigint {
    if (!/^(0|[1-9]\d*)$/.test(value)) {
        throw new TypeError(`${fieldName} is not a canonical non-negative bigint: ${value}`)
    }

    return BigInt(value)
}
